import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import CustomDropdown from './CustomDropdown'; // The multi-select dropdown

const ToggleSwitch = ({ labelLeft, labelRight, checked, onChange }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span 
        style={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#333',
          marginRight: '10px',
          opacity: checked ? 0.5 : 1, // Dim the inactive label
          transition: 'opacity 0.3s',
        }}
      >
        {labelLeft}
      </span>
      <label 
        style={{ 
          position: 'relative', 
          display: 'inline-block', 
          width: '60px', 
          height: '34px', 
          margin: '0 10px',
        }}
      >
        <input 
          type="checkbox" 
          checked={checked} 
          onChange={onChange} 
          style={{ opacity: 0, width: 0, height: 0 }} 
        />
        <span 
          style={{
            position: 'absolute',
            cursor: 'pointer',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: checked ? '#4CAF50' : '#ccc',
            transition: '.4s',
            borderRadius: '34px',
          }}
        ></span>
        <span 
          style={{
            position: 'absolute',
            content: '""',
            height: '26px',
            width: '26px',
            left: checked ? '30px' : '4px',
            bottom: '4px',
            backgroundColor: 'white',
            transition: '.4s',
            borderRadius: '50%',
          }}
        ></span>
      </label>
      <span 
        style={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#333',
          marginLeft: '10px',
          opacity: checked ? 1 : 0.5, // Dim the inactive label
          transition: 'opacity 0.3s',
        }}
      >
        {labelRight}
      </span>
    </div>
  );
};

function Score() {
  // Default predefined frameworks
  const defaultFrameworks = [
    {
      name: "Use of AI",
      keywords: ["AI"],
      concepts: ["Use AI in an ethical way"],
    },
    {
      name: "Social Enterprise & Social Entrepreneurship",
      keywords: [
        "Social Ventures",
        "Social Innovation",
        "Triple Bottom Line",
        "Community Empowerment",
        "Mission-driven Businesses",
      ],
      concepts: [
        "Creation of businesses to address social issues",
        "Blending profit-making with social impact",
        "Innovation for underrepresented communities",
      ],
    },
    {
      name: "Social Impact",
      keywords: [
        "Impact Metrics",
        "Community Development",
        "Social Value",
        "Equity-driven Strategies",
      ],
      concepts: [
        "Measuring and achieving positive effects on society",
        "Addressing systemic challenges (e.g., inequality, poverty)",
        "Stakeholder engagement and community focus",
      ],
    },
    {
      name: "Responsible Management",
      keywords: [
        "Corporate Responsibility",
        "Business Ethics",
        "Stakeholder Engagement",
        "Sustainable Decision-making",
      ],
      concepts: [
        "Ethical leadership in business practices",
        "Aligning decisions with social and environmental responsibility",
        "Stakeholder-oriented strategies",
      ],
    },
    {
      name: "Purpose-Driven Leadership",
      keywords: [
        "Mission-driven Leadership",
        "Values-based Leadership",
        "Inclusive Leadership",
        "Leading with Purpose",
      ],
      concepts: [
        "Leading organizations with a focus on values and societal contributions",
        "Inspiring change through mission alignment",
        "Fostering diversity and inclusion",
      ],
    },
    {
      name: "Environmental, Social, and Governance (ESG) Management",
      keywords: [
        "Sustainability Reporting",
        "ESG Frameworks",
        "Carbon Footprint",
        "Corporate Social Responsibility (CSR)",
      ],
      concepts: [
        "Integrating environmental and social priorities into business",
        "Governance structures that promote sustainability",
        "Transparency and accountability in reporting",
      ],
    },
    {
      name: "Sustainability",
      keywords: [
        "Circular Economy",
        "Carbon Neutrality",
        "Renewable Energy",
        "Sustainable Supply Chains",
      ],
      concepts: [
        "Long-term environmental and economic viability",
        "Resource conservation and sustainable innovation",
        "Global initiatives for climate action",
      ],
    },
    {
      name: "Diversity, Equity, and Inclusion (DEI)",
      keywords: [
        "Workplace Diversity",
        "Equity Practices",
        "Cultural Intelligence",
        "Inclusive Work Environments",
      ],
      concepts: [
        "Equity-focused organizational practices",
        "Inclusive leadership and cultural competency",
        "Mitigating bias in hiring, promotion, and decision-making",
      ],
    },
    {
      name: "Circular Economy and Regenerative Business Practices",
      keywords: [
        "Circular Design",
        "Cradle-to-Cradle",
        "Regenerative Systems",
      ],
      concepts: [
        "Product lifecycle analysis and circular supply chains",
        "Business models that regenerate ecosystems",
      ],
    },
    {
      name: "Impact Investing and Social Finance",
      keywords: [
        "Sustainable Finance",
        "Impact Bonds",
        "Venture Philanthropy",
      ],
      concepts: [
        "Funding ventures with measurable social or environmental impact",
        "ESG-aligned financial instruments",
      ],
    },
    {
      name: "Innovation and Technology for Social Good",
      keywords: [
        "Tech for Good",
        "AI for Social Impact",
        "Blockchain for Sustainability",
      ],
      concepts: [
        "Tech innovation addressing societal challenges",
        "Digital transformation for underserved communities",
      ],
    },
    {
      name: "Behavioral Economics and Nudging for Impact",
      keywords: [
        "Nudging Strategies",
        "Behavioral Insights",
        "Choice Design",
      ],
      concepts: [
        "Applying psychology to influence consumer and organizational behavior",
        "Designing 'choice architectures' for social benefits",
      ],
    },
    {
      name: "Global Citizenship and Cross-Cultural Leadership",
      keywords: [
        "Global Citizenship",
        "Intercultural Leadership",
        "Ethical Globalization",
      ],
      concepts: [
        "Social responsibility in a global context",
        "Managing cross-cultural teams with equity and ethics",
      ],
    },
    {
      name: "Climate Risk and Resilient Business Strategies",
      keywords: [
        "Climate Adaptation",
        "Resilient Strategies",
        "Environmental Risk Management",
      ],
      concepts: [
        "Adapting to climate change impacts",
        "Risk assessment for extreme weather events and environmental challenges",
      ],
    },
    {
      name: "Stakeholder Capitalism",
      keywords: [
        "Shared Value",
        "Stakeholder Engagement",
        "Stakeholder-focused Leadership",
      ],
      concepts: [
        "Balancing profits with social and environmental responsibilities",
        "Engaging diverse stakeholder groups",
      ],
    },
    {
      name: "Ethical Use of AI and Automation",
      keywords: [
        "AI Ethics",
        "Responsible Automation",
        "Technology Fairness",
      ],
      concepts: [
        "Mitigating biases in AI",
        "Ensuring technology serves diverse communities fairly",
      ],
    },
    {
      name: "Systems Thinking in Business",
      keywords: [
        "Systems Innovation",
        "Interconnected Solutions",
        "Ecosystem Thinking",
      ],
      concepts: [
        "Holistic approaches to business challenges",
        "Interdependencies between economic, social, and environmental systems",
      ],
    },
    {
      name: "Inclusive Economic Development",
      keywords: [
        "Economic Inclusion",
        "Fair Trade",
        "Community Wealth Building",
      ],
      concepts: [
        "Fair trade, poverty alleviation, and inclusive entrepreneurship",
        "Microfinance and economic empowerment",
      ],
    },
    {
      name: "Conscious Consumerism",
      keywords: [
        "Ethical Consumption",
        "Sustainable Brands",
        "Consumer Activism",
      ],
      concepts: [
        "Promoting ethical consumption",
        "Educating about the impacts of consumption choices",
      ],
    },
  ];

  const coursesList = [
    "BSM-BADM310",
    "BSM-BADM311",
    "BSM-BADM312",
    "BSM-BADM313",
    "BSM-BADM314",
    "BADM311",
    "BADM312",
    "BADM350",
    "BADM374",
    "BADM375",
    "BUS101",
    "BUS401"
  ]

  const navigate = useNavigate();

  // Attempt to load frameworks from localStorage; if not found, use default
  const storedFrameworks = "[]"; //localStorage.getItem('frameworks');
  const initialFrameworks = defaultFrameworks;

  // Attempt to load yourFrameworks from localStorage; if not found, start empty
  const storedYourFrameworks = localStorage.getItem('yourFrameworks');
  const initialYourFrameworks = storedYourFrameworks ? JSON.parse(storedYourFrameworks) : [];

  const [isOn, setIsOn] = useState(false);
  const [frameworks, setFrameworks] = useState(initialFrameworks);
  const [yourFrameworks, setYourFrameworks] = useState(initialYourFrameworks);

  const [keywords, setKeywords] = useState([""]);
  const [concepts, setConcepts] = useState([""]);
  const [courses, setCourses] = useState([]); // Now stores multiple selected courses

  // Add settings object with maxItems limit
  const [settings, setSettings] = useState({
    maxKeywords: 5,
    maxConcepts: 5,
    maxCourses: 5
  });

  const [isKeywordsCollapsed, setIsKeywordsCollapsed] = useState(false); 
  const [isConceptsCollapsed, setIsConceptsCollapsed] = useState(false); 
  const location = useLocation();
  const [isManual, setIsManual] = useState(location.state?.isManual || false);

  const [selectedFramework, setSelectedFramework] = useState(null);
  const [selectedOwnFramework, setSelectedOwnFramework] = useState(null);
  const [expandedFramework, setExpandedFramework] = useState(null);
  const [ownExpandedFramework, setOwnExpandedFramework] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);

  // For editing frameworks
  const [editingFrameworkIndex, setEditingFrameworkIndex] = useState(null);
  const [editableName, setEditableName] = useState('');
  const [editableKeywords, setEditableKeywords] = useState([]);
  const [editableConcepts, setEditableConcepts] = useState([]);
  const [editingCustom, setEditingCustom] = useState(false); 
  const [creatingNewFramework, setCreatingNewFramework] = useState(false); 

  useEffect(() => {
    if (isOn) {
      const timer = setTimeout(() => {
        navigate('/upload-framework', { state: { frameworks: frameworks, courses }});
      }, 300); // Delay for 300ms to allow CSS to take effect
      return () => clearTimeout(timer);
    }
  }, [isOn, navigate, frameworks, courses]);

  // Whenever frameworks or yourFrameworks changes, store them in local storage
  useEffect(() => {
    localStorage.setItem('frameworks', JSON.stringify(frameworks));
  }, [frameworks]);

  useEffect(() => {
    localStorage.setItem('yourFrameworks', JSON.stringify(yourFrameworks));
  }, [yourFrameworks]);

  const handleSelectFramework = (framework) => {
    if (selectedFramework === framework.name) {
      // Unselect the framework
      setSelectedFramework(null);
      setConcepts([""]);
      setKeywords([""]);
    } else {
      // Select the framework
      setSelectedFramework(framework.name);
      // Limit the number of concepts and keywords to the maximum allowed
      setConcepts(framework.concepts.slice(0, settings.maxConcepts));
      setKeywords(framework.keywords.slice(0, settings.maxKeywords));
    }
  };

  const handleOwnSelectFramework = (framework) => {
    if (selectedOwnFramework === framework.name) {
      // Unselect the framework
      setSelectedOwnFramework(null);
      setConcepts([""]);
      setKeywords([""]);
    } else {
      // Select the framework
      setSelectedOwnFramework(framework.name);
      // Limit the number of concepts and keywords to the maximum allowed
      setConcepts(framework.concepts.slice(0, settings.maxConcepts));
      setKeywords(framework.keywords.slice(0, settings.maxKeywords));
    }
  };

  const addKeyword = () => {
    if (keywords.length < settings.maxKeywords) {
      setKeywords([...keywords, '']);
    } else {
      alert(`You can only add up to ${settings.maxKeywords} keywords.`);
    }
  };
  
  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const addConcept = () => {
    if (concepts.length < settings.maxConcepts) {
      setConcepts([...concepts, '']);
    } else {
      alert(`You can only add up to ${settings.maxConcepts} concepts.`);
    }
  };
  
  const removeConcept = (index) => {
    setConcepts(concepts.filter((_, i) => i !== index));
  };

  const switchToManual = () => {
    setIsManual(!isManual);
    setConcepts([""]);
    setKeywords([""]);
  };

  const removeCourse = (index) => {
    setCourses(courses.filter((_, i) => i !== index));
  };

  const handleCollapse = (input) => {
    if (input === "keywords") {
      const keywordContainer = document.querySelector('.input-box-keywords');
      if (keywordContainer) {
        keywordContainer.style.display = isKeywordsCollapsed ? "block" : "none";
        setIsKeywordsCollapsed(!isKeywordsCollapsed); 
      }
    } else {
      const conceptContainer = document.querySelector('.input-box-concepts');
      if (conceptContainer) {
        conceptContainer.style.display = isConceptsCollapsed ? "block" : "none";
        setIsConceptsCollapsed(!isConceptsCollapsed);
      }
    }
  };

  const handleSearch = () => {
    const validKeywords = keywords.map(keyword => keyword.trim()).filter(keyword => keyword !== '');
    const validConcepts = concepts.map(concept => concept.trim()).filter(concept => concept !== '');

    if (courses.length === 0) {
      alert('Please select at least one course.');
      return;
    }

    if (validKeywords.length === 0 && validConcepts.length === 0) {
      alert('Please enter at least one valid keyword or concept.');
      return;
    }

    // Determine which framework is selected
    const frameworkName = selectedFramework || selectedOwnFramework || null;

    // Pass search parameters via state (React Router will handle it)
    const searchParams = {
      keywords: validKeywords,
      courses: courses,
      concepts: validConcepts,
      frameworkName: frameworkName,
    };

    navigate('/score-results', { state: searchParams });
  };

  const handleFrameworkClick = (framework) => {
    if (allExpanded) return; 
    if (expandedFramework === framework.name) {
      setExpandedFramework(null);
    } else {
      setExpandedFramework(framework.name);
    }
  };

  const handleOwnFrameworkClick = (framework) => {
    if (allExpanded) return; 
    if (ownExpandedFramework === framework.name) {
      setOwnExpandedFramework(null);
    } else {
      setOwnExpandedFramework(framework.name);
    }
  };
  
  const handleGlobalExpandCollapse = () => {
    setAllExpanded(!allExpanded);
    if (allExpanded) {
      setExpandedFramework(null);
    }
  };

  // Editing functions
  const startEditing = (index, isCustom=false) => {
    setEditingFrameworkIndex(index);
    if (!isCustom) {
      setEditableName(frameworks[index].name);
      setEditableKeywords([...frameworks[index].keywords]);
      setEditableConcepts([...frameworks[index].concepts]);
      setEditingCustom(false);
    } else {
      setEditableName(yourFrameworks[index].name);
      setEditableKeywords([...yourFrameworks[index].keywords]);
      setEditableConcepts([...yourFrameworks[index].concepts]);
      setEditingCustom(true);
    }
  };

  const cancelEditing = () => {
    setEditingFrameworkIndex(null);
    setEditableName('');
    setEditableKeywords([]);
    setEditableConcepts([]);
    setCreatingNewFramework(false);
  };

  const resetFramework = () => {
    localStorage.removeItem('frameworks');
    setFrameworks(defaultFrameworks);
  };

  const saveEditing = () => {
    if (!editableName.trim()) {
      alert("Framework name cannot be empty.");
      return;
    }
    
    // Filter and limit keywords and concepts
    const filteredKeywords = editableKeywords.filter(k => k.trim() !== '').slice(0, settings.maxKeywords);
    const filteredConcepts = editableConcepts.filter(c => c.trim() !== '').slice(0, settings.maxConcepts);
    
    if (!editingCustom) {
      // Updating predefined framework
      const updatedFrameworks = [...frameworks];
      updatedFrameworks[editingFrameworkIndex] = {
        ...updatedFrameworks[editingFrameworkIndex],
        name: editableName,
        keywords: filteredKeywords,
        concepts: filteredConcepts,
      };
      setFrameworks(updatedFrameworks);
    } else {
      // Updating a custom framework
      const updatedYourFrameworks = [...yourFrameworks];
      updatedYourFrameworks[editingFrameworkIndex] = {
        ...updatedYourFrameworks[editingFrameworkIndex],
        name: editableName,
        keywords: filteredKeywords,
        concepts: filteredConcepts,
      };
      setYourFrameworks(updatedYourFrameworks);
    }
    cancelEditing();
  };

  const saveAsYourOwn = () => {
    if (!editableName.trim()) {
      alert("Framework name cannot be empty.");
      return;
    }
    const newFramework = {
      name: editableName,
      keywords: editableKeywords,
      concepts: editableConcepts,
    };
    setYourFrameworks([...yourFrameworks, newFramework]);
    cancelEditing();
  };

  const addEditableKeyword = () => {
    if (editableKeywords.length < settings.maxKeywords) {
      setEditableKeywords([...editableKeywords, '']);
    } else {
      alert(`You can only add up to ${settings.maxKeywords} keywords.`);
    }
  };

  const removeEditableKeyword = (i) => {
    setEditableKeywords(editableKeywords.filter((_, idx) => idx !== i));
  };

  const addEditableConcept = () => {
    if (editableConcepts.length < settings.maxConcepts) {
      setEditableConcepts([...editableConcepts, '']);
    } else {
      alert(`You can only add up to ${settings.maxConcepts} concepts.`);
    }
  };

  const removeEditableConcept = (i) => {
    setEditableConcepts(editableConcepts.filter((_, idx) => idx !== i));
  };

  const startCreatingNewFramework = () => {
    setCreatingNewFramework(true);
    setEditingFrameworkIndex(yourFrameworks.length); 
    setEditableName('');
    setEditableKeywords(['']);
    setEditableConcepts(['']);
    setEditingCustom(true);
  };

  const saveNewFramework = () => {
    if (!editableName.trim()) {
      alert("Framework name cannot be empty.");
      return;
    }
    
    // Filter and limit keywords and concepts
    const filteredKeywords = editableKeywords.filter(k => k.trim() !== '').slice(0, settings.maxKeywords);
    const filteredConcepts = editableConcepts.filter(c => c.trim() !== '').slice(0, settings.maxConcepts);
    
    const newFramework = {
      name: editableName,
      keywords: filteredKeywords,
      concepts: filteredConcepts
    };
    setYourFrameworks([...yourFrameworks, newFramework]);
    cancelEditing();
  };

  const deleteYourFramework = (index) => {
    if (window.confirm("Are you sure you want to delete this framework?")) {
      const updatedYourFrameworks = [...yourFrameworks];
      updatedYourFrameworks.splice(index, 1);
      setYourFrameworks(updatedYourFrameworks);
    }
  };

  return (
    <div className="main-body">
      <div className="top-bar">
        <img className='logo' src="logo.svg" alt="logo" />
        <button className='manual-input' onClick={() => switchToManual()}>
          {isManual ? "Use Predefined Framework" : "Switch to Manual Input"}
        </button>
      </div>
      <div className="content">
        <div className="title">Program Competency Mapper</div>
        
        <div className="course-search-container" style={{ marginBottom: '20px' }}>
          <div style={{ padding: '40px', display: "flex", alignContent: "center", flexDirection: "column", alignItems: "center"}}>
            <CustomDropdown
              label="Select courses"
              options={coursesList}
              multiple={true}
              selectedOptions={courses} 
              onSelect={(selectedCourses) => {
                if (selectedCourses.length > settings.maxCourses) {
                  alert(`You can only select up to ${settings.maxCourses} courses at a time.`);
                  // Keep only the first 5 selected courses
                  setCourses(selectedCourses.slice(0, settings.maxCourses));
                } else {
                  setCourses(selectedCourses);
                }
              }}
            />
            <div style={{ marginTop: '25px', fontSize: '15px', color: '#666' }}>
              You can select up to {settings.maxCourses} courses at a time.
            </div>
          </div>
          <div className="course-tags" style={{ marginTop: '0px', display: 'flex', flexWrap: 'wrap', marginLeft: "92px", marginRight: "92px" }}>
            {courses.map((course, index) => (
              <div key={index} style={{ background: '#eee', padding: '12px', borderRadius: '20px', margin: '5px', display: 'flex', alignItems: 'center' }}>
                {course}
                <button
                  onClick={() => removeCourse(index)}
                  style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>

        {!isManual && (
          <div className='upload'>
            <ToggleSwitch 
              labelLeft="Predefined Framework" 
              labelRight="Upload a Framework" 
              checked={isOn} 
              onChange={(e) => setIsOn(e.target.checked)} 
            />
          </div>
        )}

        {isManual && (
          <div>
            <div className='icon-placeholder'>
              <img src="keywords.svg" alt="keywords icon" />
              <span className='keywords-word'>Keywords</span>
              <button onClick={() => handleCollapse("keywords")} className='exp-button'>
                <img
                  src={isKeywordsCollapsed ? "expand.svg" : "collapse.svg"}
                  alt={isKeywordsCollapsed ? "Expand" : "Collapse"}
                />
              </button>
            </div>

            <div className="search-container">
              <div className="input-box-keywords">
                {keywords.map((keyword, index) => (
                  <div className="input-row" key={index} style={{ display: 'flex', alignItems: 'center' }}>
                    <img src="search-logo.svg" alt="search icon"></img>
                    <input
                      type="text"
                      placeholder={`Enter a keyword (e.g., AI)`}
                      value={keyword}
                      onChange={(e) => {
                        const newKeywords = [...keywords];
                        newKeywords[index] = e.target.value;
                        setKeywords(newKeywords);
                      }}
                      style={{ flex: '1', fontSize: '18px', marginLeft: '10px', padding: "20px" }}
                    />
                    {index === keywords.length - 1 && (
                      <button
                        onClick={addKeyword}
                        style={{
                          padding: '8px 16px',
                          fontSize: '30px',
                          cursor: 'pointer',
                          color: '#56539D',
                          backgroundColor: '#F1EBFC',
                          border: 'none',
                          borderRadius: '3px',
                          marginLeft: '10px',
                        }}
                      >
                        +
                      </button>
                    )}

                    {keywords.length > 1 && (
                      <button
                        className="remove-button"
                        onClick={() => removeKeyword(index)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '30px',
                          cursor: 'pointer',
                          color: '#56539D',
                          backgroundColor: '#F8F7FA',
                          border: 'none',
                          borderRadius: '3px',
                          marginLeft: '10px',
                        }}
                      >
                        <span style={{ position: 'relative', top: '-3px' }}>-</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>


              <div className='icon-placeholder-concepts'>
                <img src="light.svg" alt="light icon"></img>
                <span className='concepts-word'>Concepts</span>
                <button onClick={() => handleCollapse("concepts")} className='exp-button'>
                  <img
                    src={isConceptsCollapsed ? "expand.svg" : "collapse.svg"}
                    alt={isConceptsCollapsed ? "Expand" : "Collapse"}
                  />
                </button>
              </div>

              <div className="input-box-concepts">
                {concepts.map((concept, index) => (
                  <div className="input-row" key={index} style={{ display: 'flex', alignItems: 'center' }}>
                    <img src="search-logo.svg" alt="search icon"></img>
                    <input
                      type="text"
                      value={concept}
                      placeholder={`Enter a concept (e.g., Use AI in an ethical and responsible manner.)`}
                      onChange={(e) => {
                        const newConcepts = [...concepts];
                        newConcepts[index] = e.target.value;
                        setConcepts(newConcepts);
                      }}
                      style={{ flex: '1', fontSize: '18px', marginLeft: '10px', padding: "20px" }}
                    />
                    {index === concepts.length - 1 && (
                      <button
                        onClick={addConcept}
                        style={{
                          padding: '8px 16px',
                          fontSize: '30px',
                          cursor: 'pointer',
                          color: '#56539D',
                          backgroundColor: '#F1EBFC',
                          border: 'none',
                          borderRadius: '3px',
                          marginLeft: '10px',
                        }}
                      >
                        +
                      </button>
                    )}

                    {concepts.length > 1 && (
                      <button
                        className="remove-button"
                        onClick={() => removeConcept(index)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '30px',
                          cursor: 'pointer',
                          color: '#56539D',
                          backgroundColor: '#F8F7FA',
                          border: 'none',
                          borderRadius: '3px',
                          marginLeft: '10px',
                        }}
                      >
                        <span style={{ position: 'relative', top: '-3px' }}>-</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!isManual && (
          <div className="predefined-frameworks">
            <div className="framework-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img src="framework.svg" alt="framework icon" />
                <span style={{ marginLeft: '10px' }}>Use Predefined Framework</span>
              </div>
              <div className='button-framework'>
                <button className='reset-button' onClick={() => resetFramework()}>Reset to default framework</button>
                <button
                  onClick={handleGlobalExpandCollapse}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    background: '#fff',
                    marginRight: '20px'
                  }}
                >
                  <img
                    src={!allExpanded ? "expanded_view.svg" : "collapsed_view.svg"}
                    alt={!allExpanded ? "Expanded View Icon" : "Collapsed View Icon"}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>{!allExpanded ? "Expanded View" : "Collapsed View"}</span>
                </button>
              </div>
            </div>

            <div className="framework-list">
              {frameworks.map((framework, index) => {
                const isExpanded = allExpanded || expandedFramework === framework.name;
                const isEditing = editingFrameworkIndex === index && !editingCustom && !creatingNewFramework;
                const isSelected = selectedFramework === framework.name;
                return (
                  <div
                    key={index}
                    className={`framework-card ${isSelected ? "selected" : ""}`}
                    style={{ 
                      cursor: isEditing ? 'default' : 'pointer', 
                      position: 'relative' ,
                      display: !isExpanded && !isEditing ? 'flex': "",
                      justifyContent: !isExpanded && !isEditing ? 'center': "",
                      alignItems: !isExpanded && !isEditing ? 'center' : '',
                      textAlign: !isExpanded && !isEditing ? 'center' : '',
                    }}
                    onClick={!isEditing ? () => handleFrameworkClick(framework) : undefined}
                  >
                    {!isEditing ? (
                      <>
                        <h3>{framework.name}</h3>
                        {isExpanded && (
                          <>
                            <div className="keywords">
                              <h4>Keywords</h4>
                              {framework.keywords.map((keyword, i) => (
                                <span key={i} className="keyword">{keyword}</span>
                              ))}
                            </div>
                            <div className="concepts">
                              <h4>Concepts</h4>
                              {framework.concepts.map((concept, i) => (
                                <span key={i} className="concept">{concept}</span>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                              <button 
                                className="select-btn" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleSelectFramework(framework);
                                }}
                              >
                                {isSelected ? "Unselect" : "Select this Framework"}
                              </button>
                              <button
                                className="edit-btn"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  startEditing(index, false);
                                }}
                              >
                                <img src="edit-btn.svg" alt="edit button logo"></img>
                                Edit
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      // Edit mode for predefined
                      <div className="edit-section" style={{ marginTop: '10px' }}>
                        <h4>Framework Name</h4>
                        <input
                          type="text"
                          value={editableName}
                          className='editable-input'
                          onChange={(e) => setEditableName(e.target.value)}
                          style={{ width: '86%', marginBottom: '10px', padding: '8px' }}
                          placeholder="Framework Name"
                        />

                        <h4>Keywords</h4>
                        {editableKeywords.map((kw, i) => (
                          <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                            <input
                              className='editable-input'
                              type="text"
                              value={kw}
                              onChange={(e) => {
                                const newKeywords = [...editableKeywords];
                                newKeywords[i] = e.target.value;
                                setEditableKeywords(newKeywords);
                              }}
                              style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                            />
                            <button className="remove-btn" onClick={() => removeEditableKeyword(i)} style={{ padding: '5px 10px' }}>-</button>
                          </div>
                        ))}
                        <button className="add-btn" onClick={addEditableKeyword}>+ Add Keyword</button>

                        <h4>Concepts</h4>
                        {editableConcepts.map((cpt, i) => (
                          <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={cpt}
                              className='editable-input'
                              onChange={(e) => {
                                const newConcepts = [...editableConcepts];
                                newConcepts[i] = e.target.value;
                                setEditableConcepts(newConcepts);
                              }}
                              style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                            />
                            <button className="remove-btn" onClick={() => removeEditableConcept(i)} style={{ padding: '5px 10px' }}>-</button>
                          </div>
                        ))}
                        <button className="add-btn" onClick={addEditableConcept} style={{ padding: '5px 10px', marginBottom: '10px' }}>+ Add Concept</button>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                          <button onClick={saveEditing} className="save-btn">Save</button>
                          <button onClick={saveAsYourOwn} className="save-btn" style={{ backgroundColor: '#2f4f4f', color: '#fff' }}>Save as Your Own</button>
                          <button onClick={cancelEditing} className="cancel-btn">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className='line-div'>
              <hr></hr>
            </div>

            {/* Your Own Frameworks Section */}
            <div className="your-own-frameworks" style={{ marginTop: '40px' }}>
              <div className="framework-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src="framework.svg" alt="framework icon" />
                  <span style={{ marginLeft: '10px' }}>Your Own Framework</span>
                </div>
                <button
                  onClick={startCreatingNewFramework}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    background: '#fff',
                    marginRight: '20px'
                  }}
                >
                  + Create New Framework
                </button>
              </div>

              {yourFrameworks.length > 0 && (
                <div className="framework-list" style={{ marginTop: '20px' }}>
                  {yourFrameworks.map((framework, index) => {
                    const isExpanded = allExpanded || ownExpandedFramework === framework.name;
                    const isEditing = editingFrameworkIndex === index && editingCustom && !creatingNewFramework;
                    return (
                      <div
                        key={index}
                        className={`framework-card ${selectedOwnFramework === framework.name ? "selected" : ""}`}
                        style={{ cursor: isEditing ? 'default' : 'pointer', 
                      position: 'relative' ,
                      display: !isExpanded && !isEditing ? 'flex': "",
                      justifyContent: !isExpanded && !isEditing ? 'center': "",
                      alignItems: !isExpanded && !isEditing ? 'center' : '',
                      textAlign: !isExpanded && !isEditing ? 'center' : '',}}
                        onClick={!isEditing ? () => handleOwnFrameworkClick(framework) : undefined}
                      >
                        {!isEditing ? (
                          <>
                            <h3>{framework.name}</h3>
                            {isExpanded && (
                              <>
                                <div className="keywords">
                                  <h4>Keywords</h4>
                                  {framework.keywords.map((keyword, i) => (
                                    <span key={i} className="keyword">{keyword}</span>
                                  ))}
                                </div>
                                <div className="concepts">
                                  <h4>Concepts</h4>
                                  {framework.concepts.map((concept, i) => (
                                    <span key={i} className="concept">{concept}</span>
                                  ))}
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                  <button 
                                    className="select-btn" 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      handleOwnSelectFramework(framework);
                                    }}
                                  >
                                    Select this Framework
                                  </button>
                                  <button
                                    className="edit-btn"
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      startEditing(index, true);
                                    }}
                                  >
                                    <img src="edit-btn.svg" alt="edit button logo"></img>
                                    Edit
                                  </button>
                                  <button
                                    className="delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteYourFramework(index);
                                    }}
                                    style={{
                                      background: '#ffdddd',
                                      border: '1px solid #ffcccc',
                                      borderRadius: '5px',
                                      cursor: 'pointer',
                                      padding: '5px 10px'
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          // Edit mode for custom frameworks
                          <div className="edit-section" style={{ marginTop: '10px' }}>
                            <h4>Framework Name</h4>
                            <input
                              type="text"
                              value={editableName}
                              className='editable-input'
                              onChange={(e) => setEditableName(e.target.value)}
                              style={{ width: '86%', marginBottom: '10px', padding: '8px' }}
                              placeholder="Framework Name"
                            />

                            <h4>Keywords</h4>
                            {editableKeywords.map((kw, i) => (
                              <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                                <input
                                  className='editable-input'
                                  type="text"
                                  value={kw}
                                  onChange={(e) => {
                                    const newKeywords = [...editableKeywords];
                                    newKeywords[i] = e.target.value;
                                    setEditableKeywords(newKeywords);
                                  }}
                                  style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                                />
                                <button className="remove-btn" onClick={() => removeEditableKeyword(i)} style={{ padding: '5px 10px' }}>-</button>
                              </div>
                            ))}
                            <button className="add-btn" onClick={addEditableKeyword}>+ Add Keyword</button>

                            <h4>Concepts</h4>
                            {editableConcepts.map((cpt, i) => (
                              <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={cpt}
                                  className='editable-input'
                                  onChange={(e) => {
                                    const newConcepts = [...editableConcepts];
                                    newConcepts[i] = e.target.value;
                                    setEditableConcepts(newConcepts);
                                  }}
                                  style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                                />
                                <button className="remove-btn" onClick={() => removeEditableConcept(i)} style={{ padding: '5px 10px' }}>-</button>
                              </div>
                            ))}
                            <button className="add-btn" onClick={addEditableConcept} style={{ padding: '5px 10px', marginBottom: '10px' }}>+ Add Concept</button>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                              <button onClick={saveEditing} className="save-btn">Save</button>
                              <button onClick={cancelEditing} className="cancel-btn">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {creatingNewFramework && (
                <div className="framework-card" style={{ marginTop: '20px' }}>
                  <div className="edit-section" style={{ marginTop: '10px' }}>
                    <h4>Framework Name</h4>
                    <input
                      type="text"
                      value={editableName}
                      className='editable-input'
                      onChange={(e) => setEditableName(e.target.value)}
                      style={{ width: '86%', marginBottom: '10px', padding: '8px' }}
                      placeholder="Framework Name"
                    />

                    <h4>Keywords</h4>
                    {editableKeywords.map((kw, i) => (
                      <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                        <input
                          className='editable-input'
                          type="text"
                          value={kw}
                          onChange={(e) => {
                            const newKeywords = [...editableKeywords];
                            newKeywords[i] = e.target.value;
                            setEditableKeywords(newKeywords);
                          }}
                          style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                        />
                        <button className="remove-btn" onClick={() => removeEditableKeyword(i)} style={{ padding: '5px 10px' }}>-</button>
                      </div>
                    ))}
                    <button className="add-btn" onClick={addEditableKeyword}>+ Add Keyword</button>

                    <h4>Concepts</h4>
                    {editableConcepts.map((cpt, i) => (
                      <div key={i} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={cpt}
                          className='editable-input'
                          onChange={(e) => {
                            const newConcepts = [...editableConcepts];
                            newConcepts[i] = e.target.value;
                            setEditableConcepts(newConcepts);
                          }}
                          style={{ flex: '1', marginRight: '5px', padding: '8px' }}
                        />
                        <button className="remove-btn" onClick={() => removeEditableConcept(i)} style={{ padding: '5px 10px' }}>-</button>
                      </div>
                    ))}
                    <button className="add-btn" onClick={addEditableConcept} style={{ padding: '5px 10px', marginBottom: '10px' }}>+ Add Concept</button>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button onClick={saveNewFramework} className="save-btn">Save</button>
                      <button onClick={cancelEditing} className="cancel-btn">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={handleSearch}
          style={{
            width: '30%',
            padding: '15px',
            fontSize: '16px',
            backgroundColor: '#000',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            margin: '20px auto 20px auto',
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          Evaluate
        </button>

        <div className='button-white'></div>
      </div>
    </div>
  );
}

export default Score;

